import React, { useEffect, useRef } from 'react';
import { useForm, ValidationError } from '@formspree/react';
import { StyleSheet, css } from 'aphrodite';
import {
  Navbar,
  FooterOverlay,
  SubHeading,
  VacancyListings,
} from '../../components';

const Vacancies = () => {
  const [state, handleSubmit] = useForm('mdkokaep');
  const confirmationRef = useRef(null);

  useEffect(() => {
    if (state.succeeded && confirmationRef.current) {
      confirmationRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      confirmationRef.current.focus();
    }
  }, [state.succeeded]);

  return (
    <>
      <Navbar />

      <div
        className={`section__padding app__bg ${css(styles.page)}`}
        id="vacancies"
      >
        <SubHeading title="Vacancy Posts" />

        <div className={css(styles.container)}>
          <div className={css(styles.hero)}>
            <p className={css(styles.badge)}>Now Hiring</p>

            <h1 className={css(styles.title)}>Build Your Career at Fortress</h1>

            <p className={css(styles.description)}>
              Explore our current opportunities below. Our newest vacancy
              always appears first.
            </p>

            <div className={css(styles.emailBox)}>
              <p className={css(styles.emailText)}>Important CV instruction</p>

              <h2 className={css(styles.emailTitle)}>Send your CV to:</h2>

              <a
                href="mailto:recruitment@fortressmenlynmaine.co.za"
                className={css(styles.emailLink)}
              >
                recruitment@fortressmenlynmaine.co.za
              </a>

              <p className={css(styles.emailNote)}>
                After completing the online application form, please email your
                updated CV to the address above.
              </p>
            </div>
          </div>

          <VacancyListings />

          <div className={css(styles.applicationSection)}>
            <div className={css(styles.formCard)}>
              {state.succeeded ? (
                <div
                  ref={confirmationRef}
                  tabIndex="-1"
                  className={css(styles.confirmationWrapper)}
                >
                  <h2 className={css(styles.confirmationTitle)}>
                    Application Sent!
                  </h2>

                  <p className={css(styles.confirmationText)}>
                    Thank you for applying to Fortress Menlyn Maine.
                  </p>

                  <div className={css(styles.confirmationEmailBox)}>
                    <p className={css(styles.confirmationSmall)}>Final step:</p>

                    <p className={css(styles.confirmationText)}>
                      Please email your CV to:
                    </p>

                    <a
                      href="mailto:recruitment@fortressmenlynmaine.co.za"
                      className={css(styles.confirmationEmailLink)}
                    >
                      recruitment@fortressmenlynmaine.co.za
                    </a>
                  </div>
                </div>
              ) : (
                <>
                  <p className={css(styles.cardKicker)}>Online Application</p>

                  <h2 className={css(styles.cardTitle)}>
                    Apply for this vacancy
                  </h2>

                  <p className={css(styles.formIntro)}>
                    Complete the form below. After submitting, please email your
                    updated CV to recruitment@fortressmenlynmaine.co.za.
                  </p>

                  <form className={css(styles.form)} onSubmit={handleSubmit}>
                    <input
                      type="hidden"
                      name="_subject"
                      value="New Vacancy Application - Fortress Menlyn Maine"
                    />

                    <div className={css(styles.formGroup)}>
                      <label htmlFor="fullName" className={css(styles.label)}>
                        Full Name
                      </label>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        placeholder="Enter your full name"
                        className={css(styles.input)}
                        required
                      />
                      <ValidationError
                        prefix="Full Name"
                        field="fullName"
                        errors={state.errors}
                      />
                    </div>

                    <div className={css(styles.formRow)}>
                      <div className={css(styles.formGroup)}>
                        <label htmlFor="email" className={css(styles.label)}>
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          placeholder="Enter your email address"
                          className={css(styles.input)}
                          required
                        />
                        <ValidationError
                          prefix="Email"
                          field="email"
                          errors={state.errors}
                        />
                      </div>

                      <div className={css(styles.formGroup)}>
                        <label htmlFor="phone" className={css(styles.label)}>
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          placeholder="Enter your phone number"
                          className={css(styles.input)}
                          required
                        />
                        <ValidationError
                          prefix="Phone"
                          field="phone"
                          errors={state.errors}
                        />
                      </div>
                    </div>

                    <div className={css(styles.formRow)}>
                      <div className={css(styles.formGroup)}>
                        <label htmlFor="position" className={css(styles.label)}>
                          Position Applying For
                        </label>
                        <select
                          id="position"
                          name="position"
                          className={css(styles.input)}
                          required
                        >
                          <option value="" className={css(styles.option)}>
                            Select position
                          </option>
                          <option
                            value="Professional Chef"
                            className={css(styles.option)}
                          >
                            Professional Chef
                          </option>
                          <option
                            value="Waiter / Delivery Rider"
                            className={css(styles.option)}
                          >
                            Waiter / Delivery Rider
                          </option>
                        </select>
                        <ValidationError
                          prefix="Position"
                          field="position"
                          errors={state.errors}
                        />
                      </div>

                      <div className={css(styles.formGroup)}>
                        <label
                          htmlFor="experience"
                          className={css(styles.label)}
                        >
                          Years of Experience
                        </label>
                        <select
                          id="experience"
                          name="experience"
                          className={css(styles.input)}
                          required
                        >
                          <option value="" className={css(styles.option)}>
                            Select experience
                          </option>
                          <option
                            value="No experience"
                            className={css(styles.option)}
                          >
                            No experience
                          </option>
                          <option
                            value="Less than 1 year"
                            className={css(styles.option)}
                          >
                            Less than 1 year
                          </option>
                          <option
                            value="1 - 2 years"
                            className={css(styles.option)}
                          >
                            1 - 2 years
                          </option>
                          <option
                            value="3 - 5 years"
                            className={css(styles.option)}
                          >
                            3 - 5 years
                          </option>
                          <option
                            value="More than 5 years"
                            className={css(styles.option)}
                          >
                            More than 5 years
                          </option>
                        </select>
                        <ValidationError
                          prefix="Experience"
                          field="experience"
                          errors={state.errors}
                        />
                      </div>
                    </div>

                    <div className={css(styles.formGroup)}>
                      <label
                        htmlFor="availability"
                        className={css(styles.label)}
                      >
                        Availability
                      </label>
                      <input
                        type="text"
                        id="availability"
                        name="availability"
                        placeholder="Example: Immediately / 1 week notice / weekends only"
                        className={css(styles.input)}
                        required
                      />
                      <ValidationError
                        prefix="Availability"
                        field="availability"
                        errors={state.errors}
                      />
                    </div>

                    <div className={css(styles.cvReminder)}>
                      <p className={css(styles.cvReminderTitle)}>CV required</p>
                      <p className={css(styles.cvReminderText)}>
                        This form does not upload CVs. Please email your CV to{' '}
                        <a
                          href="mailto:recruitment@fortressmenlynmaine.co.za"
                          className={css(styles.inlineEmail)}
                        >
                          recruitment@fortressmenlynmaine.co.za
                        </a>
                        .
                      </p>
                    </div>

                    <div className={css(styles.formGroup)}>
                      <label htmlFor="message" className={css(styles.label)}>
                        Short Motivation
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        placeholder="Tell us why you would like to join Fortress Menlyn Maine"
                        rows="5"
                        className={css(styles.textarea)}
                        required
                      />
                      <ValidationError
                        prefix="Message"
                        field="message"
                        errors={state.errors}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={state.submitting}
                      className={css(styles.submitButton)}
                    >
                      {state.submitting
                        ? 'Submitting...'
                        : 'Submit Application'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <FooterOverlay />
    </>
  );
};

const styles = StyleSheet.create({
  page: {
    minHeight: '100vh',
    background: 'var(--color-cream)',
  },

  container: {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
  },

  hero: {
    textAlign: 'center',
    maxWidth: '900px',
    margin: '0 auto 4rem',
  },

  badge: {
    display: 'inline-block',
    padding: '0.55rem 1.3rem',
    marginBottom: '1.5rem',
    border: '1px solid rgba(200, 178, 137, 0.75)',
    borderRadius: '999px',
    color: 'var(--color-golden)',
    fontFamily: 'var(--font-base)',
    fontSize: '0.95rem',
    fontWeight: 500,
    letterSpacing: '0.04em',
    textTransform: 'capitalize',
    background: 'rgba(200, 178, 137, 0.08)',
  },

  title: {
    color: 'var(--color-green)',
    fontFamily: 'var(--font-base)',
    fontSize: '64px',
    lineHeight: '80px',
    fontWeight: 400,
    letterSpacing: '0.04em',
    textTransform: 'capitalize',
    marginBottom: '1.5rem',

    '@media (max-width: 650px)': {
      fontSize: '42px',
      lineHeight: '56px',
    },

    '@media (max-width: 450px)': {
      fontSize: '35px',
      lineHeight: '48px',
    },
  },

  description: {
    color: 'var(--color-gray)',
    fontFamily: 'var(--font-alt)',
    fontWeight: 400,
    letterSpacing: '0.04em',
    lineHeight: '28px',
    fontSize: '16px',
    maxWidth: '760px',
    margin: '0 auto',

    '@media (max-width: 650px)': {
      fontSize: '14px',
    },

    '@media (max-width: 450px)': {
      fontSize: '12px',
    },
  },

  emailBox: {
    margin: '2rem auto 0',
    padding: '1.7rem',
    maxWidth: '650px',
    border: '1px solid rgba(200, 178, 137, 0.55)',
    borderRadius: '22px',
    background: 'var(--color-paper)',
    boxShadow: 'var(--shadow-soft)',
  },

  emailText: {
    color: 'var(--color-gray)',
    fontFamily: 'var(--font-alt)',
    fontSize: '16px',
    lineHeight: '28px',
    letterSpacing: '0.04em',
    marginBottom: '0.5rem',
  },

  emailTitle: {
    color: 'var(--color-golden)',
    fontFamily: 'var(--font-base)',
    fontSize: '24px',
    fontWeight: 400,
    letterSpacing: '0.04em',
    marginBottom: '0.5rem',
  },

  emailLink: {
    color: 'var(--color-green)',
    fontFamily: 'var(--font-alt)',
    fontSize: '16px',
    fontWeight: 600,
    letterSpacing: '0.02em',
    wordBreak: 'break-word',

    ':hover': {
      color: 'var(--color-golden)',
    },
  },

  emailNote: {
    marginTop: '1rem',
    color: 'var(--color-grey)',
    fontFamily: 'var(--font-alt)',
    fontSize: '14px',
    lineHeight: '24px',
    letterSpacing: '0.04em',
  },

  applicationSection: {
    maxWidth: '850px',
    margin: '0 auto',
  },

  formCard: {
    padding: '2rem',
    border: '1px solid rgba(200, 178, 137, 0.45)',
    borderRadius: '26px',
    background: 'var(--color-paper)',
    boxShadow: 'var(--shadow-soft)',

    '@media (max-width: 650px)': {
      padding: '1.4rem',
      borderRadius: '20px',
    },
  },

  cardKicker: {
    color: 'var(--color-golden)',
    fontFamily: 'var(--font-base)',
    fontSize: '20px',
    fontWeight: 400,
    letterSpacing: '0.04em',
    textTransform: 'capitalize',
    marginBottom: '0.8rem',
  },

  cardTitle: {
    color: 'var(--color-green)',
    fontFamily: 'var(--font-base)',
    fontSize: '34px',
    lineHeight: '44px',
    fontWeight: 400,
    letterSpacing: '0.04em',
    textTransform: 'capitalize',
    marginBottom: '1.5rem',

    '@media (max-width: 650px)': {
      fontSize: '28px',
      lineHeight: '38px',
    },
  },

  formIntro: {
    color: 'var(--color-gray)',
    fontFamily: 'var(--font-alt)',
    fontSize: '16px',
    lineHeight: '28px',
    letterSpacing: '0.04em',
    marginBottom: '1.8rem',

    '@media (max-width: 650px)': {
      fontSize: '14px',
    },
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.2rem',
  },

  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',

    '@media (max-width: 700px)': {
      gridTemplateColumns: '1fr',
    },
  },

  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },

  label: {
    color: 'var(--color-green)',
    fontFamily: 'var(--font-base)',
    fontSize: '16px',
    fontWeight: 500,
    letterSpacing: '0.04em',
  },

  input: {
    backgroundColor: 'var(--color-white)',
    color: 'var(--color-ink)',
    fontFamily: 'var(--font-alt)',
    fontWeight: 400,
    fontSize: '15px',
    padding: '0.9rem 1rem',
    width: '100%',
    borderRadius: '14px',
    border: '1px solid rgba(35,79,64,.2)',
    outline: 'none',
    transition: 'all 0.25s ease',
    appearance: 'none',
    WebkitAppearance: 'none',

    ':hover': {
      border: '1px solid rgba(200, 178, 137, 0.75)',
    },

    ':focus': {
      border: '1px solid var(--color-golden)',
      boxShadow: '0 0 0 4px rgba(200, 178, 137, 0.12)',
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },

    '::placeholder': {
      color: 'var(--color-grey)',
    },
  },

  option: {
    backgroundColor: '#222',
    color: 'var(--color-white)',
    fontFamily: 'var(--font-alt)',
    fontSize: '15px',
  },

  textarea: {
    backgroundColor: 'var(--color-white)',
    color: 'var(--color-ink)',
    fontFamily: 'var(--font-alt)',
    fontWeight: 400,
    fontSize: '15px',
    padding: '0.9rem 1rem',
    width: '100%',
    borderRadius: '14px',
    border: '1px solid rgba(35,79,64,.2)',
    outline: 'none',
    resize: 'vertical',
    lineHeight: '28px',
    transition: 'all 0.25s ease',

    ':hover': {
      border: '1px solid rgba(200, 178, 137, 0.75)',
    },

    ':focus': {
      border: '1px solid var(--color-golden)',
      boxShadow: '0 0 0 4px rgba(200, 178, 137, 0.12)',
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },

    '::placeholder': {
      color: 'var(--color-grey)',
    },
  },

  cvReminder: {
    padding: '1rem',
    borderRadius: '16px',
    border: '1px solid rgba(200, 178, 137, 0.4)',
    background: 'rgba(200, 178, 137, 0.08)',
  },

  cvReminderTitle: {
    color: 'var(--color-golden)',
    fontFamily: 'var(--font-base)',
    fontSize: '18px',
    letterSpacing: '0.04em',
    marginBottom: '0.4rem',
  },

  cvReminderText: {
    color: 'var(--color-gray)',
    fontFamily: 'var(--font-alt)',
    fontSize: '14px',
    lineHeight: '24px',
    letterSpacing: '0.04em',
  },

  inlineEmail: {
    color: 'var(--color-golden)',
    fontWeight: 700,
    wordBreak: 'break-word',
  },

  submitButton: {
    background: 'linear-gradient(90deg, #c8b289 0%, #05302e 90%)',
    color: 'var(--color-white)',
    fontFamily: 'var(--font-base)',
    fontWeight: 700,
    letterSpacing: '0.04em',
    lineHeight: '28px',
    fontSize: '16px',
    padding: '0.9rem 1.5rem',
    borderRadius: '14px',
    border: 'none',
    outline: 'none',
    cursor: 'pointer',
    transition: 'all 0.25s ease',
    marginTop: '0.5rem',

    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 10px 25px rgba(200, 178, 137, 0.22)',
    },

    ':disabled': {
      opacity: 0.7,
      cursor: 'not-allowed',
      transform: 'none',
    },
  },

  confirmationWrapper: {
    padding: '3rem 2rem',
    borderRadius: '22px',
    textAlign: 'center',
    border: '1px solid rgba(200, 178, 137, 0.45)',
    background:
      'linear-gradient(135deg, rgba(5, 48, 46, 0.9), rgba(12, 12, 12, 0.95))',
    outline: 'none',
  },

  confirmationTitle: {
    color: 'var(--color-golden)',
    fontFamily: 'var(--font-base)',
    fontSize: '36px',
    fontWeight: 400,
    letterSpacing: '0.04em',
    marginBottom: '1rem',
  },

  confirmationText: {
    color: 'var(--color-white)',
    fontFamily: 'var(--font-alt)',
    fontSize: '16px',
    lineHeight: '28px',
    letterSpacing: '0.04em',
  },

  confirmationEmailBox: {
    marginTop: '2rem',
    padding: '1.3rem',
    borderRadius: '18px',
    background: 'rgba(200, 178, 137, 0.1)',
    border: '1px solid rgba(200, 178, 137, 0.35)',
  },

  confirmationSmall: {
    color: 'var(--color-golden)',
    fontFamily: 'var(--font-base)',
    fontSize: '18px',
    letterSpacing: '0.04em',
    marginBottom: '0.5rem',
  },

  confirmationEmailLink: {
    display: 'inline-block',
    marginTop: '0.6rem',
    color: 'var(--color-golden)',
    fontFamily: 'var(--font-alt)',
    fontSize: '16px',
    fontWeight: 700,
    letterSpacing: '0.02em',
    wordBreak: 'break-word',
  },
});

export default Vacancies;
